/**
 * K7.1: SHA-256 (FIPS 180-4) of a text's UTF-8 bytes, as 64 lowercase hex digits: what the pages
 * vote and the room checks a snapshot by. FNV-1a's 32 bits catch a bug, not a forger: a state that
 * hashes to the majority's vote is cheap to make, and a snapshot the room keeps on that vote sets
 * every joiner from it. `crypto.subtle` is asynchronous and the room and the match are not (their
 * tests step them by hand), so the digest is computed here; `test/sha256.test.ts` proves it equal
 * to Node's. No import: the Worker takes it as the pages do.
 */
const ROUND_CONSTANTS = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const INITIAL_STATE = [
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
];

const BLOCK_BYTES = 64;
/** The padded tail ends in the message's length in bits, 8 bytes. */
const LENGTH_BYTES = 8;
const TWO_TO_32 = 0x1_0000_0000;

const encoder = new TextEncoder();

const rotateRight = (word: number, bits: number) => (word >>> bits) | (word << (32 - bits));

/** One 64-byte block of `bytes` from `offset` folded into `state`. */
function compress(state: Uint32Array, schedule: Uint32Array, bytes: Uint8Array, offset: number) {
  for (let index = 0; index < 16; index += 1) {
    const at = offset + index * 4;
    schedule[index] =
      ((bytes[at] ?? 0) << 24) |
      ((bytes[at + 1] ?? 0) << 16) |
      ((bytes[at + 2] ?? 0) << 8) |
      (bytes[at + 3] ?? 0);
  }
  for (let index = 16; index < 64; index += 1) {
    const early = schedule[index - 15] ?? 0;
    const late = schedule[index - 2] ?? 0;
    const sigma0 = rotateRight(early, 7) ^ rotateRight(early, 18) ^ (early >>> 3);
    const sigma1 = rotateRight(late, 17) ^ rotateRight(late, 19) ^ (late >>> 10);
    schedule[index] = (schedule[index - 16] ?? 0) + sigma0 + (schedule[index - 7] ?? 0) + sigma1;
  }
  let [a = 0, b = 0, c = 0, d = 0, e = 0, f = 0, g = 0, h = 0] = state;
  for (let index = 0; index < 64; index += 1) {
    const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
    const choice = (e & f) ^ (~e & g);
    const first = (h + sum1 + choice + (ROUND_CONSTANTS[index] ?? 0) + (schedule[index] ?? 0)) | 0;
    const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
    const majority = (a & b) ^ (a & c) ^ (b & c);
    const second = (sum0 + majority) | 0;
    h = g;
    g = f;
    f = e;
    e = (d + first) | 0;
    d = c;
    c = b;
    b = a;
    a = (first + second) | 0;
  }
  const worked = [a, b, c, d, e, f, g, h];
  for (let index = 0; index < 8; index += 1) {
    state[index] = (state[index] ?? 0) + (worked[index] ?? 0);
  }
}

export function sha256Hex(text: string): string {
  const bytes = encoder.encode(text);
  const state = Uint32Array.from(INITIAL_STATE);
  const schedule = new Uint32Array(64);
  const wholeBlocks = Math.floor(bytes.length / BLOCK_BYTES);
  for (let block = 0; block < wholeBlocks; block += 1) {
    compress(state, schedule, bytes, block * BLOCK_BYTES);
  }
  const rest = bytes.length - wholeBlocks * BLOCK_BYTES;
  const tail = new Uint8Array(
    rest + 1 + LENGTH_BYTES > BLOCK_BYTES ? 2 * BLOCK_BYTES : BLOCK_BYTES,
  );
  tail.set(bytes.subarray(wholeBlocks * BLOCK_BYTES));
  tail[rest] = 0x80;
  const bits = bytes.length * 8;
  const length = new DataView(tail.buffer);
  length.setUint32(tail.length - 8, Math.floor(bits / TWO_TO_32));
  length.setUint32(tail.length - 4, bits >>> 0);
  for (let offset = 0; offset < tail.length; offset += BLOCK_BYTES) {
    compress(state, schedule, tail, offset);
  }
  return Array.from(state, (word) => word.toString(16).padStart(8, "0")).join("");
}
